import type { StringGUID } from "@pdfking/tfunc"
import uuid from "@pdfking/workflow/utils/uuid"
import navigate from "../routes/navigate"
import { PROMPT_VIEW } from "../routes/urls"

type PromptResult = {
  id: StringGUID
  message: string | undefined
  result: string | undefined
}

// Store for resolving/rejecting promises
const pending_dialogs = new Map<
  string,
  {
    reject: (reason?: any) => void
    resolve: (value: PromptResult) => void
  }
>()

// Event listener for dialog results
document.addEventListener("alert-dialog-result", ((e: CustomEvent) => {
  const { id, message, result } = e.detail as PromptResult
  const dialog = pending_dialogs.get(id)

  if (dialog) {
    dialog.resolve({ id, message, result })
    pending_dialogs.delete(id)
  }
}) as EventListener)

/**
 * Shows a confirmation dialog with custom buttons
 * @param options Configuration object
 * @param options.title The title/message to display in the dialog
 * @param options.buttons Record of button keys to button labels (e.g., { abort: 'Cancel', confirm: 'OK' })
 * @param options.default_button The key of the button to style as primary (defaults to last button)
 * @param options.message_name If present, shows a text field and includes its value in the result
 * @returns Promise that resolves with the key of the button pressed (or object with button + message if message_name is set), or null if dialog was closed
 */
export default ({
  buttons,
  default_button,
  message_name,
  title,
}: {
  buttons: Record<string, string>
  default_button?: string
  message_name?: string
  title: string
}): Promise<PromptResult> => {
  return new Promise((resolve, reject) => {
    // Generate unique ID for this dialog
    const id = uuid()

    // Store the promise handlers
    pending_dialogs.set(id, { reject, resolve })

    // Navigate to prompt view with parameters
    navigate(PROMPT_VIEW, {
      buttons,
      default_button,
      id,
      message_name,
      title,
    })
  })
}
